import apiClient from './api';

export type PublishingPlatform = 'youtube' | 'instagram' | 'facebook' | 'whatsapp' | 'tiktok' | 'x';

export interface ScheduledPost {
  jobId: string;
  platform: PublishingPlatform;
  title: string;
  scheduledFor: string;
  status: string;
}

export interface BulkScheduleRequest {
  platform: PublishingPlatform;
  accountId: string;
  clipRefs: { jobId: string; clipId: string }[];
  cadence: { unit: 'hours' | 'days'; amount: number };
  startAt: string;
  titleTemplate?: string;
  privacyStatus?: 'private' | 'unlisted' | 'public';
  selfDeclaredMadeForKids?: boolean;
  captionTemplate?: string;
  hashtags?: string[];
}

export interface BulkScheduleResult {
  success: boolean;
  created: { clipId: string; jobId: string; scheduledFor: string }[];
  failed: { clipId: string; error: string }[];
}

export interface RecurringSchedule {
  scheduleId: string;
  platform: PublishingPlatform;
  accountId: string;
  projectId: string;
  frequency: 'daily' | 'weekly';
  daysOfWeek?: number[];
  metadataTemplate: {
    titleTemplate?: string;
    privacyStatus?: 'private' | 'unlisted' | 'public';
    selfDeclaredMadeForKids?: boolean;
    captionTemplate?: string;
    hashtags?: string[];
  };
  isActive: boolean;
  nextRunAt: string;
  lastRunAt?: string;
  lastSkipReason?: string;
}

export interface CreateRecurringScheduleRequest {
  platform: PublishingPlatform;
  accountId: string;
  projectId: string;
  frequency: 'daily' | 'weekly';
  daysOfWeek?: number[];
  startAt: string;
  titleTemplate?: string;
  privacyStatus?: 'private' | 'unlisted' | 'public';
  selfDeclaredMadeForKids?: boolean;
  captionTemplate?: string;
  hashtags?: string[];
}

export const publishingApi = {
  getScheduled: async (): Promise<{ success: boolean; posts: ScheduledPost[] }> => {
    const response = await apiClient.get('/publishing/scheduled');
    return response.data;
  },

  reschedule: async (platform: PublishingPlatform, jobId: string, scheduledFor: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.patch(`/publishing/scheduled/${platform}/${jobId}`, { scheduledFor });
    return response.data;
  },

  cancel: async (platform: PublishingPlatform, jobId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.patch(`/publishing/scheduled/${platform}/${jobId}`, { cancel: true });
    return response.data;
  },

  bulkSchedule: async (payload: BulkScheduleRequest): Promise<BulkScheduleResult> => {
    const response = await apiClient.post('/publishing/bulk-schedule', payload);
    return response.data;
  },

  getRecurring: async (): Promise<{ success: boolean; schedules: RecurringSchedule[] }> => {
    const response = await apiClient.get('/publishing/recurring');
    return response.data;
  },

  createRecurring: async (payload: CreateRecurringScheduleRequest): Promise<{ success: boolean; schedule?: RecurringSchedule; message?: string }> => {
    const response = await apiClient.post('/publishing/recurring', payload);
    return response.data;
  },

  toggleRecurring: async (scheduleId: string, isActive: boolean): Promise<{ success: boolean; schedule?: RecurringSchedule }> => {
    const response = await apiClient.patch(`/publishing/recurring/${scheduleId}`, { isActive });
    return response.data;
  },

  deleteRecurring: async (scheduleId: string): Promise<{ success: boolean; message?: string }> => {
    const response = await apiClient.delete(`/publishing/recurring/${scheduleId}`);
    return response.data;
  },
};

export default publishingApi;
